/**
 * Hero Section Component
 * Netflix-style featured film banner with backdrop
 */

import { useState, useEffect } from '@wordpress/element';

export default function HeroSection({ film, onTrailerClick }) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const t = window.rialtoData?.translations || {};

    if (!film) return null;

    const hasTrailer = film.trailer_embed || film.trailer_url;
    const backdropUrl = film.backdrop_url || film.poster;

    const formatDuration = (mins) => {
        if (!mins || mins <= 0) return null;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h > 0 && m > 0) return `${h}h ${m}m`;
        if (h > 0) return `${h}h`;
        return `${m}m`;
    };

    const handleTrailerClick = () => {
        if (onTrailerClick && hasTrailer) {
            onTrailerClick(film);
        }
    };

    return (
        <section className="rialto-hero">
            {/* Background Image */}
            <div className="rialto-hero-backdrop">
                {backdropUrl && (
                    <img
                        src={backdropUrl}
                        alt=""
                        onLoad={() => setImageLoaded(true)}
                        className={imageLoaded ? 'loaded' : ''}
                    />
                )}
                <div className="rialto-hero-gradient" />
            </div>

            {/* Content */}
            <div className="rialto-hero-content">
                {/* Featured Badge */}
                {film.pick_of_week && (
                    <div className="rialto-hero-badge">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        {t.pickOfWeek || 'Pick of the Week'}
                    </div>
                )}

                {/* Title */}
                <h1 className="rialto-hero-title">{film.title}</h1>

                {/* Meta Info */}
                <div className="rialto-hero-meta">
                    {film.rating && (
                        <span className="rialto-hero-rating">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#f5c518">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            {parseFloat(film.rating).toFixed(1)}
                            {film.votes > 0 && (
                                <span className="rialto-hero-votes">
                                    ({film.votes.toLocaleString()} votes)
                                </span>
                            )}
                        </span>
                    )}
                    {film.year && <span>{film.year}</span>}
                    {film.duration > 0 && <span>{formatDuration(film.duration)}</span>}
                    {film.certification && (
                        <span className="rialto-hero-cert">{film.certification}</span>
                    )}
                </div>

                {/* Genres */}
                {film.genres?.length > 0 && (
                    <div className="rialto-hero-genres">
                        {film.genres.join(' • ')}
                    </div>
                )}

                {/* Synopsis */}
                {film.synopsis && (
                    <p className="rialto-hero-synopsis">{film.synopsis}</p>
                )}

                {/* Director & Cast */}
                <div className="rialto-hero-credits">
                    {film.director && (
                        <p>
                            <strong>{t.director || 'Director'}:</strong> {film.director}
                        </p>
                    )}
                    {film.actors && (
                        <p>
                            <strong>{t.starring || 'Starring'}:</strong> {film.actors}
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="rialto-hero-actions">
                    {hasTrailer && (
                        <button
                            className="rialto-btn rialto-btn-primary"
                            onClick={handleTrailerClick}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                            {t.watchTrailer || 'Watch Trailer'}
                        </button>
                    )}
                    <a href={film.url} className="rialto-btn rialto-btn-secondary">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                        {t.moreInfo || 'More Info'}
                    </a>
                </div>
            </div>
        </section>
    );
}
